import { useIsMutating } from "@tanstack/react-query"
import { useRouterState } from "@tanstack/react-router"
import { IconRefreshClockwiseFill18 } from "nucleo-ui-fill-18"
import { useEffect, useRef } from "react"

import { Button } from "@/components/ui/button"
import { useAppUpdate } from "@/hooks/use-app-update"

/**
 * Tells a visitor a new version has shipped, and quietly applies it at the next
 * safe moment — a navigation, with nothing being saved — so nobody has to be
 * asked to refresh.
 */
function AppUpdateBanner() {
  const { isUpdateAvailable, applyUpdate } = useAppUpdate()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const pendingMutations = useIsMutating()
  const pathnameWhenDetected = useRef<string | null>(null)

  useEffect(() => {
    if (!isUpdateAvailable) {
      pathnameWhenDetected.current = null
      return
    }

    pathnameWhenDetected.current ??= pathname

    if (pathname !== pathnameWhenDetected.current && pendingMutations === 0) {
      applyUpdate()
    }
  }, [isUpdateAvailable, pathname, pendingMutations, applyUpdate])

  if (!isUpdateAvailable) {
    return null
  }

  return (
    <div
      data-slot="app-update-banner"
      role="status"
      className="flex items-center justify-center gap-3 border-b border-border bg-muted px-4 py-2 text-sm text-foreground"
    >
      <span>A new version of the application is available.</span>
      <Button size="xs" variant="outline" onClick={applyUpdate}>
        <IconRefreshClockwiseFill18 data-icon="inline-start" />
        Update now
      </Button>
    </div>
  )
}

export { AppUpdateBanner }
